// src/pages/ChatBot/query-logs/corpus-pdf-viewer.tsx
//
// Scrollable pdf.js viewer for one corpus document inside the Custom Answer
// workbench. Follows the house pdf.js pattern (SMRTDocsFieldPlacementModal):
// dynamic legacy-build import + the pinned jsdelivr worker. Each page renders
// as a canvas plus a selectable text layer so the parent pane can offer
// "Cite selection" on highlighted passages.

import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy, PDFPageProxy, PageViewport } from 'pdfjs-dist';

type PdfJsModule = typeof import('pdfjs-dist/legacy/build/pdf');

// Same worker build + CDN as SMRTDocsFieldPlacementModal — keep in lockstep
// with the installed pdfjs-dist version.
const PDFJS_WORKER_SRC =
  'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js';

// Combined horizontal breathing room inside the scroll pane, in px.
const PAGE_GUTTER_PX = 24;
// Used when the pane has no measurable width yet (e.g. display:none race).
const FALLBACK_PAGE_WIDTH_PX = 720;

async function loadPdfJs(): Promise<PdfJsModule> {
  const pdfjs = await import(
    /* webpackChunkName: "pdfjs" */ 'pdfjs-dist/legacy/build/pdf'
  );
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;
  }
  return pdfjs;
}

async function fetchPdfBytes(fileName: string, signal: AbortSignal): Promise<Uint8Array> {
  const res = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/chatbot/download/${encodeURIComponent(fileName)}`,
    { credentials: 'include', signal },
  );
  if (!res.ok) throw new Error(`Failed to download document (${res.status})`);
  return new Uint8Array(await res.arrayBuffer());
}

function createPageWrapper(pageNumber: number, viewport: PageViewport): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'qlc-pdf-page';
  wrapper.dataset.pageNumber = String(pageNumber);
  wrapper.style.width = `${Math.floor(viewport.width)}px`;
  wrapper.style.height = `${Math.floor(viewport.height)}px`;
  return wrapper;
}

// Renders the page bitmap at devicePixelRatio resolution while keeping the
// CSS size at viewport size, so the text layer coordinates still line up.
async function renderPageCanvas(page: PDFPageProxy, viewport: PageViewport): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');
  const pixelRatio = window.devicePixelRatio || 1;
  canvas.width = Math.floor(viewport.width * pixelRatio);
  canvas.height = Math.floor(viewport.height * pixelRatio);
  canvas.style.width = `${Math.floor(viewport.width)}px`;
  canvas.style.height = `${Math.floor(viewport.height)}px`;
  const context = canvas.getContext('2d');
  if (!context) return canvas;
  const transform = pixelRatio !== 1 ? [pixelRatio, 0, 0, pixelRatio, 0, 0] : undefined;
  await page.render({ canvasContext: context, viewport, transform }).promise;
  return canvas;
}

async function renderPageTextLayer(
  pdfjs: PdfJsModule,
  page: PDFPageProxy,
  wrapper: HTMLElement,
  viewport: PageViewport,
): Promise<void> {
  const textLayer = document.createElement('div');
  textLayer.className = 'qlc-pdf-textlayer';
  // pdf.js 3.11 positions text runs in calc(var(--scale-factor) * Npx) units
  // and errors out when the variable is missing.
  textLayer.style.setProperty('--scale-factor', String(viewport.scale));
  wrapper.appendChild(textLayer);
  const textContent = await page.getTextContent();
  await pdfjs.renderTextLayer({
    textContentSource: textContent,
    container: textLayer,
    viewport,
    textDivs: [],
  }).promise;
}

// `refitToken` is a re-fit trigger only: when it changes the render effect
// re-runs and re-measures the pane, re-rendering pages at the new fit width
// (e.g. after the answer pane collapses and widens the document pane). It is
// intentionally not read inside the effect body.
export default function CorpusPdfViewer({
  fileName,
  refitToken,
}: {
  fileName: string;
  refitToken?: number;
}) {
  const pagesRef = useRef<HTMLDivElement | null>(null);
  const generationRef = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const generation = ++generationRef.current;
    const controller = new AbortController();
    let pdfDocument: PDFDocumentProxy | null = null;
    const container = pagesRef.current;
    if (container) container.replaceChildren();
    setIsLoading(true);
    setLoadError(null);

    const isStale = () => generation !== generationRef.current;

    (async () => {
      const [pdfjs, bytes] = await Promise.all([
        loadPdfJs(),
        fetchPdfBytes(fileName, controller.signal),
      ]);
      if (isStale()) return;
      // getDocument itself is not abortable; if this load went stale while it
      // was in flight, cleanup saw pdfDocument === null and destroyed nothing —
      // destroy here or the document (and its worker resources) leaks.
      const doc = await pdfjs.getDocument({ data: bytes }).promise;
      if (isStale() || !container) {
        doc.destroy().catch(() => undefined);
        return;
      }
      pdfDocument = doc;
      const targetWidth =
        Math.max(container.clientWidth - PAGE_GUTTER_PX, 0) || FALLBACK_PAGE_WIDTH_PX;
      for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber++) {
        if (isStale()) return;
        const page = await doc.getPage(pageNumber);
        if (isStale()) return;
        const baseViewport = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: targetWidth / baseViewport.width });
        const wrapper = createPageWrapper(pageNumber, viewport);
        const canvas = await renderPageCanvas(page, viewport);
        if (isStale()) return;
        wrapper.appendChild(canvas);
        container.appendChild(wrapper);
        await renderPageTextLayer(pdfjs, page, wrapper, viewport);
      }
      if (isStale()) return;
      setIsLoading(false);
    })().catch((error: unknown) => {
      if (isStale()) return;
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setLoadError(error instanceof Error ? error.message : 'Failed to render the document.');
      setIsLoading(false);
    });

    return () => {
      // Invalidate in-flight work before aborting/destroying so the loop's
      // stale checks (and the catch) bail instead of touching state.
      generationRef.current++;
      controller.abort();
      if (pdfDocument) pdfDocument.destroy().catch(() => undefined);
    };
  }, [fileName, refitToken]);

  return (
    <div className="qlc-pdf-scroll">
      {isLoading && (
        <div className="qlc-pdf-status">
          <span className="qlc-spinner" />
          Loading document…
        </div>
      )}
      {loadError && <div className="qlc-error-banner">{loadError}</div>}
      <div ref={pagesRef} className="qlc-pdf-pages" />
    </div>
  );
}
